import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectPasswordExpired, selectToken, selectUser } from "../store/authSlice";
import Images from "../utils/Images";
import axiosInstance from "../utils/Api";

export default function ResetPassword() {

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // The session created at sign-in lives in Redux.
    const token = useSelector(selectToken);
    const user = useSelector(selectUser);
    const passwordExpired = useSelector(selectPasswordExpired);

    const [email] = useState(location.state?.email || user?.email || "");
    const [userId] = useState(user?._id || "");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Live password requirement checks
    const passwordChecks = {
        length: newPassword.length >= 6,
        uppercase: /[A-Z]/.test(newPassword),
        lowercase: /[a-z]/.test(newPassword),
        number: /[0-9]/.test(newPassword),
        special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword),
    };

    const allRequirementsMet = Object.values(passwordChecks).every(Boolean);

    const handleResetPassword = async () => {
        setError("");

        if (!newPassword || newPassword.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }

        if (!passwordChecks.uppercase) {
            setError("Password must contain at least 1 capital letter.");
            return;
        }

        if (!passwordChecks.lowercase) {
            setError("Password must contain at least 1 small letter.");
            return;
        }

        if (!passwordChecks.number) {
            setError("Password must contain at least 1 number.");
            return;
        }

        if (!passwordChecks.special) {
            setError("Password must contain at least 1 special character.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (!token) {
            // No authenticated session to reset against.
            navigate("/", { replace: true });
            return;
        }

        try {
            setLoading(true);

            // Same shape as the admin flow's reset-password call —
            // axiosInstance automatically attaches the Bearer token.
            const response = await axiosInstance.put("admin-user-management/reset-password", {
                _id: userId,
                username: email,
                password: newPassword,
            });

            const result = response.data;


            if (!result.status) {
                throw new Error(result.message || "Unable to reset password.");
            }


            // Clear the expired session (Redux + localStorage) and require a fresh login.
            dispatch(logout());
            navigate("/", { replace: true });
        } catch (err) {
            console.log("Error in Reset Api :", err?.response);

            const message =
                err.response?.data?.error ||
                err.response?.data?.data?.message ||
                err.response?.data?.message ||
                err.message ||
                "Something went wrong. Please try again.";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const RequirementItem = ({ met, label }) => (
        <div className="flex items-center gap-2">
            <span
                className="flex items-center justify-center w-[16px] h-[16px] rounded-full shrink-0 transition-colors"
                style={{
                    background: met ? "#00C2B2" : "#E5EEF2",
                }}
            >
                {met ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                ) : (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#AEBECB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="4" />
                    </svg>
                )}
            </span>
            <span
                className="xl-body text-[12px] transition-colors"
                style={{ color: met ? "#08213D" : "#8CA0B3" }}
            >
                {label}
            </span>
        </div>
    );

    // This screen is only for a signed-in session whose password has expired.
    if (!token) return <Navigate to="/" replace />;
    if (!passwordExpired) return <Navigate to="/home" replace />;

    return (
        <main
            className="min-h-dvh flex items-center justify-center px-4"
            style={{
                background:
                    "radial-gradient(120% 90% at 50% -10%, #0C86B8 0%, #04698F 45%, #06213F 100%)",
                paddingTop: "max(2rem, env(safe-area-inset-top))",
                paddingBottom: "max(2rem, env(safe-area-inset-bottom))",
            }}
        >
            {/* Same brand fonts as the login screen — Space Grotesk for display,
                Inter for body, IBM Plex Mono for the manifest strip. */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
                .xl-display { font-family: 'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }
                @keyframes xl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
                .xl-status-dot { animation: xl-pulse 2s ease-in-out infinite; }
            `}</style>

            <section className="w-full max-w-[400px] flex flex-col items-center">
                {/* Logo badge — same white rounded square used on the login screen */}
                <div className="relative z-20 w-[92px] h-[92px] rounded-[26px] bg-white shadow-[0_18px_40px_rgba(4,105,143,0.45)] flex items-center justify-center p-[8px] mb-[-46px]">
                    <img
                        src={Images.logo1}
                        alt="XP StockFlow"
                        className="w-full h-full object-contain rounded-[16px]"
                    />
                </div>

                <div className="w-full bg-white rounded-[28px] shadow-[0_30px_80px_rgba(4,33,63,0.35)] pt-[58px] px-6 pb-0 overflow-hidden">
                    <div className="text-center mb-7">
                        <p className="xl-mono text-[10px] font-medium tracking-[0.28em] uppercase text-[#00A4E3] mb-1.5">
                            Password Reset Required
                        </p>
                        <h1 className="xl-display text-[24px] font-bold tracking-tight text-[#08213D]">
                            Set a new password
                        </h1>
                        {/* <p className="xl-body mt-1.5 text-[12.5px] leading-snug text-[#5B6B7C]">
                            Your Stock Flow password has expired. Choose a new one to continue.
                        </p> */}
                    </div>

                    <label
                        htmlFor="reset-new-password"
                        className="xl-body block text-[13px] font-semibold text-[#3D4C5E] mb-2"
                    >
                        New password <span className="text-[#00A4E3]">*</span>
                    </label>

                    <div className="flex items-center h-[52px] rounded-[16px] border border-[#DCEAF1] bg-[#F9FCFD] overflow-hidden transition focus-within:border-[#00A4E3] focus-within:shadow-[0_0_0_4px_rgba(0,164,227,0.12)]">
                        <div className="w-[46px] h-full flex items-center justify-center shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A4E3" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="4" y="10" width="16" height="11" rx="2.5" />
                                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                            </svg>
                        </div>

                        <input
                            id="reset-new-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            autoFocus
                            disabled={loading}
                            value={newPassword}
                            placeholder="Enter new password"
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setError("");
                            }}
                            className="xl-body flex-1 h-full pr-2 text-[15px] outline-none bg-transparent disabled:opacity-60"
                        />

                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            disabled={loading}
                            className="w-[42px] h-full flex items-center justify-center text-[#8CA0B3] hover:text-[#00A4E3] active:text-[#00A4E3] transition shrink-0 disabled:opacity-60"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                        >
                            {showPassword ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 4.22-5.94M9.9 4.24A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <path d="M1 1l22 22" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Live password requirement checklist */}
                    <div className="mt-3 mb-1 grid grid-cols-2 gap-y-2 gap-x-2 bg-[#F9FCFD] border border-[#DCEAF1] rounded-[14px] px-3.5 py-3">
                        <RequirementItem met={passwordChecks.length} label="At least 6 characters" />
                        <RequirementItem met={passwordChecks.uppercase} label="1 capital letter" />
                        <RequirementItem met={passwordChecks.lowercase} label="1 small letter" />
                        <RequirementItem met={passwordChecks.number} label="1 number" />
                        <RequirementItem met={passwordChecks.special} label="1 special character" />
                    </div>

                    <label
                        htmlFor="reset-confirm-password"
                        className="xl-body block text-[13px] font-semibold text-[#3D4C5E] mb-2 mt-4"
                    >
                        Confirm password <span className="text-[#00A4E3]">*</span>
                    </label>

                    <div className="flex items-center h-[52px] rounded-[16px] border border-[#DCEAF1] bg-[#F9FCFD] overflow-hidden transition focus-within:border-[#00A4E3] focus-within:shadow-[0_0_0_4px_rgba(0,164,227,0.12)]">
                        <div className="w-[46px] h-full flex items-center justify-center shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00A4E3" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="4" y="10" width="16" height="11" rx="2.5" />
                                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                            </svg>
                        </div>

                        <input
                            id="reset-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            autoComplete="new-password"
                            disabled={loading}
                            value={confirmPassword}
                            placeholder="Confirm new password"
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setError("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleResetPassword();
                                }
                            }}
                            className="xl-body flex-1 h-full pr-2 text-[15px] outline-none bg-transparent disabled:opacity-60"
                        />

                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            disabled={loading}
                            className="w-[42px] h-full flex items-center justify-center text-[#8CA0B3] hover:text-[#00A4E3] active:text-[#00A4E3] transition shrink-0 disabled:opacity-60"
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                        >
                            {showConfirmPassword ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 4.22-5.94M9.9 4.24A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <path d="M1 1l22 22" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="flex items-start gap-1.5 mt-3">
                            <span className="text-red-500 text-xs leading-4">⚠</span>
                            <p className="xl-body text-[12px] text-red-500 font-medium leading-4">
                                {error}
                            </p>
                        </div>
                    )}

                    <button
                        onClick={handleResetPassword}
                        disabled={loading || !allRequirementsMet}
                        className="xl-display mt-6 w-full h-[54px] rounded-[16px] text-white font-bold uppercase text-[14px] tracking-[0.08em] shadow-[0_14px_30px_rgba(0,164,227,0.4)] active:translate-y-0 hover:-translate-y-[1px] transition disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                        style={{ background: "linear-gradient(135deg, #00A4E3 0%, #04698F 100%)" }}
                    >
                        {loading ? (
                            <>
                                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                Please wait
                            </>
                        ) : (
                            <>Reset password →</>
                        )}
                    </button>

                    {/* Waybill / manifest tear-off strip — the signature detail, shared with login */}
                    <div className="relative mt-7 -mx-6">
                        <div className="absolute left-0 right-0 top-0 border-t-2 border-dashed border-[#DCEAF1]" />
                        <div className="absolute -top-[9px] -left-[9px] w-[18px] h-[18px] rounded-full" style={{ background: "#04698F" }} />
                        <div className="absolute -top-[9px] -right-[9px] w-[18px] h-[18px] rounded-full" style={{ background: "#04698F" }} />

                        <div className="px-6 pt-4 pb-5 flex items-center justify-center gap-2">
                            <span className="xl-status-dot w-[6px] h-[6px] rounded-full bg-[#00C2B2] shrink-0" />
                            <p className="xl-mono text-[10px] tracking-[0.14em] uppercase text-[#5B6B7C]">
                                Access · Authorized Logistics Staff Only
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 text-center text-[12px] xl-body font-medium text-white/70">
                    XP StockFlow · Logistics &amp; Management
                </div>
            </section>
        </main>
    );
}